import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import AppLayout from '../../components/AppLayout';
import useConfirm from '../../components/ConfirmDialog/useConfirm';
import { S, Icon, Icons, BtnPrimary, BtnSecondary, EmptyState, TH, TD, formatDate, formatDateTime } from '../regureStyles';
import { canAddWorkflow } from '../../utils/rbac';
import { useSelector } from 'react-redux';

const BASE_URL = process.env.REACT_APP_BASE_URL || '';

// ─── Mock data (commented out — no longer used for initialization) ───
// const MOCK_RULES = [
//   { id: 'wr_001', name: 'Auto-assign E&O claims to Senior Adjuster', trigger: 'claim_created', enabled: true, priority: 1, executionCount: 48, lastExecuted: '2026-02-28', conditions: [{ field: 'lineOfBusiness', operator: 'eq', value: 'professional_liability' }], actions: [{ type: 'assign_claim', config: { userId: 'Michael Chen' } }] },
//   { id: 'wr_002', name: 'Escalate critical priority claims', trigger: 'claim_created', enabled: true, priority: 2, executionCount: 12, lastExecuted: '2026-02-27', conditions: [{ field: 'priority', operator: 'eq', value: 'critical' }], actions: [{ type: 'send_notification', config: { to: 'claims_manager', message: 'Critical claim received' } }, { type: 'set_reserve', config: { amount: 50000 } }] },
//   { id: 'wr_003', name: 'Flag high-value claims for review', trigger: 'claim_updated', enabled: true, priority: 3, executionCount: 6, lastExecuted: '2026-02-25', conditions: [{ field: 'reserveAmount', operator: 'gt', value: '100000' }], actions: [{ type: 'flag_for_review', config: {} }, { type: 'change_status', config: { status: 'review' } }] },
//   { id: 'wr_004', name: 'Auto-close approved claims after 30 days', trigger: 'claim_status_changed', enabled: false, priority: 10, executionCount: 3, lastExecuted: '2026-01-15', conditions: [{ field: 'status', operator: 'eq', value: 'approved' }, { field: 'daysSinceUpdate', operator: 'gte', value: '30' }], actions: [{ type: 'change_status', config: { status: 'closed' } }] },
//   { id: 'wr_005', name: 'Notify broker on claim status change', trigger: 'claim_status_changed', enabled: true, priority: 5, executionCount: 127, lastExecuted: '2026-03-01', conditions: [], actions: [{ type: 'send_notification', config: { to: 'broker', template: 'status_change' } }] },
// ];
// const MOCK_EXEC_LOG = [
//   { id: 'el_001', rule: 'Auto-assign E&O claims to Senior Adjuster', trigger: 'claim.created — CL-2026-0147', result: 'success', timestamp: '2026-03-01 09:30:12', duration: '43ms' },
//   { id: 'el_002', rule: 'Flag high-value claims for review',        trigger: 'claim.updated — CL-2026-0144',  result: 'success', timestamp: '2026-03-01 09:15:04', duration: '28ms' },
//   { id: 'el_003', rule: 'Notify broker on claim status change',     trigger: 'claim.status_changed — CL-2026-0146', result: 'success', timestamp: '2026-02-28 17:20:33', duration: '156ms' },
//   { id: 'el_004', rule: 'Auto-assign E&O claims to Senior Adjuster',trigger: 'claim.created — CL-2026-0143',  result: 'skipped', timestamp: '2026-02-28 14:05:11', duration: '12ms' },
//   { id: 'el_005', rule: 'Flag high-value claims for review',        trigger: 'claim.updated — CL-2026-0141',  result: 'error',   timestamp: '2026-02-27 11:44:22', duration: '—' },
// ];

// Options carry an i18n key rather than a hardcoded English label so the
// dropdowns match the user's locale. Keys resolve to en/fr/de under
// screens.workflows.trigger.* and screens.workflows.action.*.
// Phase 0A-4. Mirrors the backend producer registry (backend/services/workflowTriggers.js):
// a trigger may be offered here IF AND ONLY IF a producer emits it.
//
// Removed - no emitter exists anywhere in the backend, so a rule built on
// one of these was silently inert, with no error and no log:
//   document_uploaded    semantics pending (received/uploaded/classified/attached)
//   document_classified  the AI pipeline emits no workflow event
//   manual               no producer and no manual-run endpoint
//
// 0B follow-up: fetch GET /workflow-rules/trigger-catalogue so this list
// derives from the registry instead of mirroring it.
const TRIGGER_OPTIONS = [
  { value: 'claim_created',          labelKey: 'screens.workflows.trigger.claim_created' },
  { value: 'claim_updated',          labelKey: 'screens.workflows.trigger.claim_updated' },
  { value: 'claim_status_changed',   labelKey: 'screens.workflows.trigger.claim_status_changed' },
  { value: 'claim_assigned',         labelKey: 'screens.workflows.trigger.claim_assigned' },
  { value: 'claim_stale',            labelKey: 'screens.workflows.trigger.claim_stale' },
  { value: 'policy_created',         labelKey: 'screens.workflows.trigger.policy_created' },
  { value: 'policy_status_changed',  labelKey: 'screens.workflows.trigger.policy_status_changed' },
  { value: 'scheduled',              labelKey: 'screens.workflows.trigger.scheduled' },
];

// Every value the WorkflowRule model's trigger enum accepts, for DISPLAY.
//
// This is deliberately wider than TRIGGER_OPTIONS. A trigger is only
// OFFERED when a producer emits it, but rules carrying a retired or
// producer-less trigger still exist in the database, and the badge used to
// fall through to the raw enum — a rule listed its trigger as the literal
// string "document_uploaded". Offering a trigger and being able to name one
// are different questions; conflating them made stored data unreadable.
const TRIGGER_LABEL_KEYS = {
  claim_created:         'screens.workflows.trigger.claim_created',
  claim_updated:         'screens.workflows.trigger.claim_updated',
  claim_status_changed:  'screens.workflows.trigger.claim_status_changed',
  claim_assigned:        'screens.workflows.trigger.claim_assigned',
  claim_stale:           'screens.workflows.trigger.claim_stale',
  document_uploaded:     'screens.workflows.trigger.document_uploaded',
  document_classified:   'screens.workflows.trigger.document_classified',
  policy_created:        'screens.workflows.trigger.policy_created',
  policy_status_changed: 'screens.workflows.trigger.policy_status_changed',
  scheduled:             'screens.workflows.trigger.scheduled',
  manual:                'screens.workflows.trigger.manual',
};

// Resolves through i18n, which carries the tenant's configured vocabulary,
// so a tenant that calls a claim a "Case" reads "Case Status Changed".
// Falls back to the raw value only for a trigger newer than this map.
const triggerLabel = (t, value) => {
  const key = TRIGGER_LABEL_KEYS[value];
  return key ? t(key) : String(value || '—');
};

const ACTION_OPTIONS = [
  { value: 'assign_claim',              labelKey: 'screens.workflows.action.assign_claim' },
  { value: 'change_status',             labelKey: 'screens.workflows.action.change_status' },
  { value: 'send_notification',         labelKey: 'screens.workflows.action.send_notification' },
  { value: 'add_note',                  labelKey: 'screens.workflows.action.add_note' },
  { value: 'set_priority',              labelKey: 'screens.workflows.action.set_priority' },
  { value: 'set_reserve',               labelKey: 'screens.workflows.action.set_reserve' },
  { value: 'flag_for_review',           labelKey: 'screens.workflows.action.flag_for_review' },
  { value: 'escalate',                  labelKey: 'screens.workflows.action.escalate' },
  { value: 'create_risk_signal',        labelKey: 'screens.workflows.action.create_risk_signal' },
  // Offering this to a tenant with bordereaux off lets an admin build a rule
  // whose action can never run — the endpoint behind it 403s. `feature` is read
  // by the ACTION_OPTIONS filter at render time.
  { value: 'generate_bordereaux',       labelKey: 'screens.workflows.action.generate_bordereaux', feature: 'bordereaux' },
  { value: 'calculate_tax',             labelKey: 'screens.workflows.action.calculate_tax' },
  { value: 'flag_for_filing',           labelKey: 'screens.workflows.action.flag_for_filing' },
  { value: 'generate_document',         labelKey: 'screens.workflows.action.generate_document' },
  { value: 'send_for_signature',        labelKey: 'screens.workflows.action.send_for_signature' },
  { value: 'verify_eligibility',        labelKey: 'screens.workflows.action.verify_eligibility' },
  { value: 'generate_stamping_report',  labelKey: 'screens.workflows.action.generate_stamping_report' },
];

const FIELD_OPTIONS = [
  'lineOfBusiness', 'status', 'priority', 'reserveAmount', 'policyNumber',
  'daysSinceUpdate', 'claimant.company', 'assignedTo',
  'state', 'carrierType', 'hasUnfiledSurplusLinesPolicies',
];

const OPERATOR_OPTIONS = ['eq', 'ne', 'gt', 'lt', 'gte', 'lte', 'in', 'nin', 'contains', 'regex'];

const RESULT_STYLES = {
  success: { bg: '#dcfce7', color: '#15803d', labelKey: 'screens.workflows.resultSuccess' },
  skipped: { bg: '#fef3c7', color: '#92400e', labelKey: 'screens.workflows.resultSkipped' },
  error:   { bg: '#fee2e2', color: '#991b1b', labelKey: 'screens.workflows.resultError' },
};

// ─── Empty rule template ──────────────────────────────────
const emptyRule = () => ({
  name: '', trigger: 'claim_created', priority: 10, enabled: true,
  conditions: [{ field: 'lineOfBusiness', operator: 'eq', value: '' }],
  actions: [{ type: 'assign_claim', config: {} }],
});

// ─── Rule Form Panel ─────────────────────────────────────
function RuleFormPanel({ rule, onClose, onSave }) {
  // Tenant features, read from the same slice useFeature reads, so a rule
  // cannot be built around an action the tenant's modules do not support.
  // Only the CREATE dropdown is filtered — an existing rule that already uses
  // such an action still renders its own name in the list below, because
  // hiding the label would misrepresent what the rule does.
  const features = useSelector(state => state?.tenantConfig?.config?.features) || {};
  const { t } = useTranslation();
  const [form, setForm] = useState(rule || emptyRule());

  const addCondition = () => setForm(f => ({ ...f, conditions: [...f.conditions, { field: 'lineOfBusiness', operator: 'eq', value: '' }] }));
  const removeCondition = (i) => setForm(f => ({ ...f, conditions: f.conditions.filter((_, idx) => idx !== i) }));
  const updateCondition = (i, key, val) => setForm(f => ({ ...f, conditions: f.conditions.map((c, idx) => idx === i ? { ...c, [key]: val } : c) }));

  const addAction = () => setForm(f => ({ ...f, actions: [...f.actions, { type: 'send_notification', config: {} }] }));
  const removeAction = (i) => setForm(f => ({ ...f, actions: f.actions.filter((_, idx) => idx !== i) }));
  const updateAction = (i, key, val) => setForm(f => ({ ...f, actions: f.actions.map((a, idx) => idx === i ? { ...a, [key]: val } : a) }));

  const inputStyle = {
    padding: '8px 12px', background: '#fff', border: `1px solid ${S.borderLight}`,
    borderRadius: 8, fontSize: 13, color: S.textPrimary, outline: 'none',
    fontFamily: 'inherit', width: '100%', boxSizing: 'border-box',
  };
  const labelStyle = { fontSize: 11, fontWeight: 600, color: S.textMuted, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 6, display: 'block' };

  return (
    <div style={{
      position: 'fixed', right: 0, top: 0, bottom: 0, width: 520,
      background: '#fff', boxShadow: '-8px 0 40px rgba(0,0,0,0.12)',
      display: 'flex', flexDirection: 'column', zIndex: 1000,
    }}>
      <div style={{ padding: '20px 24px', borderBottom: `1px solid ${S.borderLight}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ fontSize: 17, fontWeight: 700, color: S.textPrimary }}>{rule ? t('screens.workflows.editRule') : t('screens.workflows.newRuleTitle')}</div>
        <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
          <Icon name="x" color={S.textMuted} size={18} />
        </button>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '24px' }}>
        {/* Name */}
        <div style={{ marginBottom: 20 }}>
          <label style={labelStyle}>{t('screens.workflows.ruleName')}</label>
          <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
            placeholder={t('screens.workflows.ruleNamePh')} style={inputStyle} />
        </div>

        {/* Trigger + Priority */}
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 16, marginBottom: 20 }}>
          <div>
            <label style={labelStyle}>{t('screens.workflows.triggerEvent')}</label>
            <select value={form.trigger} onChange={e => setForm(f => ({ ...f, trigger: e.target.value }))} style={inputStyle}>
              {TRIGGER_OPTIONS.map(o => <option key={o.value} value={o.value}>{t(o.labelKey)}</option>)}
            </select>
          </div>
          <div>
            <label style={labelStyle}>{t('screens.workflows.priority')}</label>
            <input type="number" min="1" max="100" value={form.priority}
              onChange={e => setForm(f => ({ ...f, priority: parseInt(e.target.value) || 1 }))} style={inputStyle} />
          </div>
        </div>

        {/* Conditions */}
        <div style={{ marginBottom: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <label style={{ ...labelStyle, marginBottom: 0 }}>{t('screens.workflows.conditions')}</label>
            <button onClick={addCondition} style={{ padding: '4px 12px', background: S.bgCard, border: `1px solid ${S.borderLight}`, borderRadius: 6, fontSize: 12, cursor: 'pointer', color: S.textSecondary }}>{t('screens.workflows.add')}</button>
          </div>
          {form.conditions.length === 0 && (
            <div style={{ padding: '12px', background: S.bgCard, border: `1px dashed ${S.borderLight}`, borderRadius: 8, fontSize: 13, color: S.textMuted, textAlign: 'center' }}>
              {t('screens.workflows.noConditions')}
            </div>
          )}
          {form.conditions.map((c, i) => {
            const isNumericOp = ['gt', 'lt', 'gte', 'lte'].includes(c.operator);
            const isNumericValue = !isNumericOp || (c.value !== '' && !isNaN(Number(c.value)));
            return (
              <div key={i} style={{ marginBottom: 8 }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <select value={c.field} onChange={e => updateCondition(i, 'field', e.target.value)} style={{ ...inputStyle, flex: 1.5 }}>
                    {FIELD_OPTIONS.map(f => <option key={f} value={f}>{f}</option>)}
                  </select>
                  <select value={c.operator} onChange={e => updateCondition(i, 'operator', e.target.value)} style={{ ...inputStyle, flex: 1 }}>
                    {OPERATOR_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                  <input
                    type={isNumericOp ? 'number' : 'text'}
                    step={isNumericOp ? 'any' : undefined}
                    value={c.value}
                    onChange={e => updateCondition(i, 'value', e.target.value)}
                    placeholder={isNumericOp ? t('screens.workflows.conditionNumericPh') : t('screens.workflows.conditionValuePh')}
                    style={{
                      ...inputStyle,
                      flex: 1.5,
                      borderColor: isNumericValue ? inputStyle?.borderColor || S.borderLight : '#ef4444',
                    }}
                  />
                  <button onClick={() => removeCondition(i)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: '4px' }}>
                    <Icon name="x" color="#94a3b8" size={14} />
                  </button>
                </div>
                {!isNumericValue && (
                  <div style={{ fontSize: 11, color: '#ef4444', marginTop: 4, marginLeft: 4 }}>
                    {t('screens.workflows.numericValueWarning')}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Actions */}
        <div style={{ marginBottom: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <label style={{ ...labelStyle, marginBottom: 0 }}>{t('screens.workflows.actions')}</label>
            <button onClick={addAction} style={{ padding: '4px 12px', background: S.bgCard, border: `1px solid ${S.borderLight}`, borderRadius: 6, fontSize: 12, cursor: 'pointer', color: S.textSecondary }}>{t('screens.workflows.add')}</button>
          </div>
          {form.actions.map((a, i) => (
            <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
              <select value={a.type} onChange={e => updateAction(i, 'type', e.target.value)} style={{ ...inputStyle, flex: 2 }}>
                {ACTION_OPTIONS.filter(o => !o.feature || features?.[o.feature] === true)
                  .map(o => <option key={o.value} value={o.value}>{t(o.labelKey)}</option>)}
              </select>
              <button onClick={() => removeAction(i)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: '4px' }}>
                <Icon name="x" color="#94a3b8" size={14} />
              </button>
            </div>
          ))}
        </div>

        {/* Enabled */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px', background: S.bgCard, border: `1px solid ${S.borderLight}`, borderRadius: 10 }}>
          <div
            onClick={() => setForm(f => ({ ...f, enabled: !f.enabled }))}
            style={{
              width: 44, height: 24, borderRadius: 12,
              background: form.enabled ? S.burgundy : '#cbd5e1',
              cursor: 'pointer', position: 'relative', transition: 'background 0.2s', flexShrink: 0,
            }}
          >
            <div style={{
              position: 'absolute', top: 2, left: form.enabled ? 22 : 2,
              width: 20, height: 20, borderRadius: '50%', background: '#fff',
              transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
            }} />
          </div>
          <span style={{ fontSize: 13, color: S.textPrimary, fontWeight: 500 }}>
            {form.enabled ? t('screens.workflows.ruleIsEnabled') : t('screens.workflows.ruleIsDisabled')}
          </span>
        </div>
      </div>

      <div style={{ padding: '16px 24px', borderTop: `1px solid ${S.borderLight}`, display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
        <BtnSecondary onClick={onClose}>{t('screens.workflows.cancel')}</BtnSecondary>
        <BtnPrimary onClick={() => onSave && onSave(form)} disabled={!form.name.trim()}>
          {rule ? t('screens.workflows.saveChanges') : t('screens.workflows.createRule')}
        </BtnPrimary>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// WORKFLOW RULES SCREEN
// ═══════════════════════════════════════════════════════════
const WorkflowRulesScreen = () => {
  const { t } = useTranslation();
  const { confirm, ConfirmPortal } = useConfirm();
  const loggedinUser = useSelector(state => state.loggedinUser);
  const userRole = loggedinUser?.userInfo?.data?.user?.role;
  
  const [rules, setRules] = useState([]);
  const [execLog, setExecLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState('');
  const [activeTab, setActiveTab] = useState('rules');
  const [showForm, setShowForm] = useState(false);
  const [editRule, setEditRule] = useState(null);
  const [search, setSearch] = useState('');

  const fetchRules = useCallback(async () => {
    try {
      const resp = await axios.get(`${BASE_URL}/workflow-rules`, { withCredentials: true });
      if (resp.data?.data) {
        setRules(resp.data.data.map(r => ({ ...r, id: r._id || r.id })));
      }
    } catch { setFetchError(t('screens.workflows.failedLoad')); } finally { setLoading(false); }
  }, [t]);

  const fetchExecLog = useCallback(async () => {
    try {
      const resp = await axios.get(`${BASE_URL}/workflow-rules/executions`, { params: { limit: 50 }, withCredentials: true });
      if (resp.data?.data) setExecLog(resp.data.data);
    } catch { setFetchError(t('screens.workflows.failedLoadLog')); }
  }, [t]);

  useEffect(() => { fetchRules(); fetchExecLog(); }, [fetchRules, fetchExecLog]);

  const TABS = [
    { id: 'rules',   label: t('screens.workflows.tabRules') },
    { id: 'execlog', label: t('screens.workflows.tabExecLog') },
  ];

  const filtered = rules.filter(r => !search || r.name.toLowerCase().includes(search.toLowerCase()));

  const toggleEnabled = async (id) => {
    const rule = rules.find(r => r.id === id);
    if (!rule) return;
    const newEnabled = !rule.enabled;
    // Optimistic update
    setRules(prev => prev.map(r => r.id === id ? { ...r, enabled: newEnabled } : r));
    try {
      await axios.patch(`${BASE_URL}/workflow-rules/${id}`, { enabled: newEnabled }, { withCredentials: true });
    } catch {
      // Revert on failure
      setRules(prev => prev.map(r => r.id === id ? { ...r, enabled: !newEnabled } : r));
      setFetchError(t('screens.workflows.failedToggle'));
    }
  };

  const handleSave = async (form) => {
    // Numeric operators need numeric values, or the backend silently skips
    // (Number("50K") = NaN). Reject client-side so users see the reason.
    const badCondition = (form.conditions || []).find(c =>
      ['gt', 'lt', 'gte', 'lte'].includes(c.operator) && (c.value === '' || isNaN(Number(c.value)))
    );
    if (badCondition) {
      setFetchError(t('screens.workflows.conditionErr', { field: badCondition.field, op: badCondition.operator, val: badCondition.value }));
      return;
    }
    try {
      if (editRule && editRule.id) {
        const resp = await axios.patch(`${BASE_URL}/workflow-rules/${editRule.id}`, form, { withCredentials: true });
        const updated = resp.data?.data;
        if (updated) {
          setRules(prev => prev.map(r => r.id === editRule.id ? { ...updated, id: updated._id || updated.id } : r));
        }
      } else {
        const resp = await axios.post(`${BASE_URL}/workflow-rules`, form, { withCredentials: true });
        const created = resp.data?.data;
        if (created) {
          setRules(prev => [...prev, { ...created, id: created._id || created.id }]);
        }
      }
      setShowForm(false);
      setEditRule(null);
    } catch {
      setFetchError(t('screens.workflows.failedSave'));
    }
  };

  const openEdit = (rule) => { setEditRule(rule); setShowForm(true); };

  const handleDelete = async (rule) => {
    const ok = await confirm({
      title: t('screens.workflows.confirmDeleteTitle', { defaultValue: 'Delete workflow rule?' }),
      message: t('screens.workflows.confirmDelete', {
        defaultValue: `"${rule.name}" will be permanently removed.`,
        name: rule.name,
      }),
      confirmText: t('common.delete', { defaultValue: 'Delete' }),
      variant: 'danger',
    });
    if (!ok) return;
    try {
      await axios.delete(`${BASE_URL}/workflow-rules/${rule.id}`, { withCredentials: true });
      setRules(prev => prev.filter(r => r.id !== rule.id));
    } catch {
      setFetchError(t('screens.workflows.failedDelete'));
    }
  };

  const handleDuplicate = (rule) => {
    setEditRule(null);
    setShowForm(true);
    // Pre-fill form with a copy after a tick so the panel picks it up as "create"
    setTimeout(() => {
      const panel = document.querySelector('[data-rule-form]');
      // We'll handle this via editRule = null + passing initial data through a ref
    }, 0);
    // Simpler: just open create with the rule's data minus id
    const copy = { ...rule, name: `${rule.name} (${t('screens.workflows.duplicate').toLowerCase()})`, id: undefined, _id: undefined, executionCount: 0, lastExecutedAt: null };
    setEditRule(null);
    setShowForm(false);
    setTimeout(() => { setEditRule(copy); setShowForm(true); }, 50);
  };

  const [menuOpen, setMenuOpen] = useState(null);

  return (
    <AppLayout>
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>

        {/* Header */}
        <header style={{ padding: '20px 32px', borderBottom: `1px solid ${S.borderLight}`, background: 'rgba(255,255,255,0.85)', backdropFilter: 'blur(20px)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <div>
            <div style={{ fontSize: 12, color: S.textMuted, display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
              {t('screens.workflows.breadcrumb')} {Icons.chevronRight(S.textMuted, 12)} <span style={{ color: S.textPrimary, fontWeight: 500 }}>{t('screens.workflows.sectionTitle')}</span>
            </div>
            <div style={{ fontSize: 22, fontWeight: 700, color: S.textPrimary }}>{t('screens.workflows.title')}</div>
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 14px', background: S.bgCard, border: `1px solid ${S.borderLight}`, borderRadius: 10 }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#10b981' }} />
              <span style={{ fontSize: 13, color: S.textSecondary }}>{t('screens.workflows.activeRules', { count: rules.filter(r => r.enabled).length })}</span>
            </div>
            {canAddWorkflow(userRole) ? <BtnPrimary onClick={() => { setEditRule(null); setShowForm(true); }}>
              <Icon name="plus" color="#fff" size={16} /> {t('screens.workflows.newRule')}
            </BtnPrimary> : null}
          </div>
        </header>

        {/* Tabs */}
        <div style={{ display: 'flex', padding: '0 32px', borderBottom: `1px solid ${S.borderLight}`, background: 'rgba(255,255,255,0.6)', flexShrink: 0 }}>
          {TABS.map(t => (
            <button key={t.id} onClick={() => setActiveTab(t.id)} style={{
              padding: '12px 18px', fontSize: 13,
              fontWeight: activeTab === t.id ? 600 : 500,
              color: activeTab === t.id ? S.burgundy : S.textMuted,
              background: 'none', border: 'none',
              borderBottom: activeTab === t.id ? `2px solid ${S.burgundy}` : '2px solid transparent',
              cursor: 'pointer', transition: 'all 0.15s',
            }}>{t.label}</button>
          ))}
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '24px 32px' }}>
          <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
          {fetchError && (
            <div style={{ marginBottom: 16, padding: '12px 16px', background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, color: '#b91c1c', fontSize: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              {fetchError}
              <button onClick={() => setFetchError('')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#b91c1c', fontSize: 18, lineHeight: 1 }}>×</button>
            </div>
          )}
          {activeTab === 'rules' && (
            loading ? (
              <div style={{display:'flex',justifyContent:'center',alignItems:'center',padding:60}}><div style={{width:36,height:36,border:'3px solid #e2e8f0',borderTopColor:'#7c3aed',borderRadius:'50%',animation:'spin 0.8s linear infinite'}}/></div>
            ) : <>
              {/* Search */}
              <div style={{ position: 'relative', maxWidth: 360, marginBottom: 20 }}>
                <div style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>
                  <Icon name="search" color="#94a3b8" size={15} />
                </div>
                <input value={search} onChange={e => setSearch(e.target.value)}
                  placeholder={t('screens.workflows.searchRules')}
                  style={{ width: '100%', padding: '9px 12px 9px 36px', background: '#fff', border: `1px solid ${S.borderLight}`, borderRadius: 8, fontSize: 13, color: S.textPrimary, outline: 'none', boxSizing: 'border-box' }}
                />
              </div>

              {filtered.length === 0 ? (
                <EmptyState icon="git-branch" title={t('screens.workflows.noRulesTitle')} description={t('screens.workflows.noRulesDesc')} action={<BtnPrimary onClick={() => setShowForm(true)}><Icon name="plus" color="#fff" size={16} /> {t('screens.workflows.createFirstRule')}</BtnPrimary>} />
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {filtered.map(rule => (
                    <div key={rule.id} style={{ background: rule.enabled ? '#fff' : S.bgCard, border: `1px solid ${rule.enabled ? S.borderLight : '#e2e8f0'}`, borderRadius: 14, padding: '18px 22px', opacity: rule.enabled ? 1 : 0.7 }}>
                      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                            <div style={{ fontSize: 15, fontWeight: 600, color: S.textPrimary }}>{rule.name}</div>
                            <span style={{ padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 600, background: '#e0e7ff', color: '#4338ca', whiteSpace: 'nowrap' }}>
                              {triggerLabel(t, rule.trigger)}
                            </span>
                            <span style={{ padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 600, background: S.bgCard, color: S.textMuted, border: `1px solid ${S.borderLight}` }}>
                              {t('screens.workflows.priorityLabel', { n: rule.priority })}
                            </span>
                          </div>

                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
                            {rule.conditions.map((c, i) => (
                              <span key={i} style={{ padding: '3px 10px', borderRadius: 6, fontSize: 12, background: '#fef3c7', color: '#92400e', border: '1px solid #fde68a' }}>
                                {c.field} {c.operator} {c.value}
                              </span>
                            ))}
                            {rule.conditions.length === 0 && (
                              <span style={{ padding: '3px 10px', borderRadius: 6, fontSize: 12, background: S.bgCard, color: S.textMuted, border: `1px solid ${S.borderLight}` }}>{t('screens.workflows.noConditions')}</span>
                            )}
                          </div>

                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                            {rule.actions.map((a, i) => (
                              <span key={i} style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '3px 10px', borderRadius: 6, fontSize: 12, background: 'rgba(128,0,32,0.07)', color: S.burgundy }}>
                                <span style={{ width: 5, height: 5, borderRadius: '50%', background: S.burgundy }} />
                                {(() => { const o = ACTION_OPTIONS.find(x => x.value === a.type); return o ? t(o.labelKey) : a.type; })()}
                              </span>
                            ))}
                          </div>
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 12, flexShrink: 0 }}>
                          {/* Toggle */}
                          <div
                            onClick={() => toggleEnabled(rule.id)}
                            style={{
                              width: 44, height: 24, borderRadius: 12,
                              background: rule.enabled ? S.burgundy : '#cbd5e1',
                              cursor: 'pointer', position: 'relative', transition: 'background 0.2s',
                            }}
                          >
                            <div style={{
                              position: 'absolute', top: 2, left: rule.enabled ? 22 : 2,
                              width: 20, height: 20, borderRadius: '50%', background: '#fff',
                              transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                            }} />
                          </div>
                          {canAddWorkflow(userRole) ? <div style={{ position: 'relative' }}>
                            <button onClick={(e) => { e.stopPropagation(); setMenuOpen(menuOpen === rule.id ? null : rule.id); }}
                              style={{ padding: '4px 8px', background: 'none', border: `1px solid ${S.borderLight}`, borderRadius: 6, fontSize: 16, cursor: 'pointer', color: S.textMuted, lineHeight: 1 }}>⋮</button>
                            {menuOpen === rule.id && (
                              <div style={{ position: 'absolute', right: 0, top: 32, background: '#fff', border: `1px solid ${S.borderLight}`, borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', zIndex: 50, minWidth: 140, overflow: 'hidden' }}>
                                <button onClick={() => { openEdit(rule); setMenuOpen(null); }} style={{ display: 'block', width: '100%', padding: '10px 16px', background: 'none', border: 'none', textAlign: 'left', fontSize: 13, cursor: 'pointer', color: S.textPrimary }}
                                  onMouseEnter={e => e.target.style.background = S.bgCard} onMouseLeave={e => e.target.style.background = 'none'}>{t('screens.workflows.edit')}</button>
                                <button onClick={() => { handleDuplicate(rule); setMenuOpen(null); }} style={{ display: 'block', width: '100%', padding: '10px 16px', background: 'none', border: 'none', textAlign: 'left', fontSize: 13, cursor: 'pointer', color: S.textPrimary }}
                                  onMouseEnter={e => e.target.style.background = S.bgCard} onMouseLeave={e => e.target.style.background = 'none'}>{t('screens.workflows.duplicate')}</button>
                                <button onClick={() => { handleDelete(rule); setMenuOpen(null); }} style={{ display: 'block', width: '100%', padding: '10px 16px', background: 'none', border: 'none', textAlign: 'left', fontSize: 13, cursor: 'pointer', color: '#dc2626', borderTop: `1px solid ${S.borderLight}` }}
                                  onMouseEnter={e => e.target.style.background = '#fef2f2'} onMouseLeave={e => e.target.style.background = 'none'}>{t('screens.workflows.delete')}</button>
                              </div>
                            )}
                          </div> : null}
                          <div style={{ fontSize: 11, color: S.textMuted, textAlign: 'right' }}>
                            {t('screens.workflows.executions', { count: rule.executionCount || 0 })}
                            {(rule.lastExecutedAt || rule.lastExecuted) && <><br />{t('screens.workflows.lastRun', { when: formatDate(rule.lastExecutedAt || rule.lastExecuted) })}</>}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {activeTab === 'execlog' && (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  {[
                    { k: 'ts', label: t('screens.workflows.colTimestamp') },
                    { k: 'rule', label: t('screens.workflows.colRule') },
                    { k: 'ctx', label: t('screens.workflows.colTriggerContext') },
                    { k: 'res', label: t('screens.workflows.colResult') },
                    { k: 'dur', label: t('screens.workflows.colDuration') },
                  ].map(h => (
                    <TH key={h.k}>{h.label}</TH>
                  ))}
                </tr>
              </thead>
              <tbody>
                {execLog.map((log, i) => {
                  const rs = RESULT_STYLES[log.result] || RESULT_STYLES.success;
                  return (
                    <tr key={log.id} style={{ borderBottom: `1px solid ${S.borderLight}`, background: i % 2 === 0 ? '#fff' : S.bgCard }}>
                      <TD style={{ fontSize: 12, fontFamily: 'monospace', color: S.textMuted }}>{formatDateTime(log.timestamp)}</TD>
                      <TD style={{ fontWeight: 500, color: S.textPrimary }}>{log.rule}</TD>
                      <TD>
                        <span style={{ fontSize: 12, color: S.textSecondary }}>
                          {triggerLabel(t, log.trigger)}{log.entityRef ? ` — ${log.entityRef}` : ''}
                        </span>
                      </TD>
                      <TD>
                        <span style={{ padding: '3px 10px', borderRadius: 6, fontSize: 11, fontWeight: 600, background: rs.bg, color: rs.color }}>
                          {t(rs.labelKey)}
                        </span>
                      </TD>
                      <TD style={{ fontSize: 12, fontFamily: 'monospace', color: S.textMuted }}>
                        {typeof log.durationMs === 'number' ? `${log.durationMs}ms` : '—'}
                      </TD>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Slide-in form panel */}
      {showForm && (
        <>
          <div onClick={() => { setShowForm(false); setEditRule(null); }} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.25)', zIndex: 999 }} />
          <RuleFormPanel rule={editRule} onClose={() => { setShowForm(false); setEditRule(null); }} onSave={handleSave} />
        </>
      )}
      {ConfirmPortal}
    </AppLayout>
  );
};

export default WorkflowRulesScreen;
